import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { getTimesheetByIdData } from "../../api/Services/assetServices";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

interface TimesheetRow {
  type: string;
  familyAssetId?: number | null;
  familyName?: string | "",
  assetName: string;
  owner: string;
  groupCode: string;
  harmony: string;
  description: string;
  totalCharge?: number | null;
  timesheet?: Record<string, string | number>;
}

interface Rig {
  rigName: string;
  rows: TimesheetRow[];
}

interface TimesheetData {
  currentLocationId: number;
  countryName: string;
  regionId: number;
  regionName: string;
  projectId: number;
  projectName: string;
  type: string;
  fromdate: string;
  todate: string;
  month?: string;
  year?: string;
  totalHours?: number;
  totalCharge?: number;
  rigs: Rig[];
}

const TimesheetView: React.FC<{
  timeSheetId: number | null;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ timeSheetId, setOpen }) => {
  const [data, setData] = useState<TimesheetData | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchTimesheet = async () => {
      if (!timeSheetId) return;
      dispatch(setLoading(true));

      try {
        const response = await getTimesheetByIdData(timeSheetId);
        if (response.status.code === 200 && response.data) {
          const d = response.data;

          const normalizedData: TimesheetData = {
            currentLocationId: d.currentLocationId ?? 0,
            countryName: d.countryName ?? "",
            regionId: d.regionId ?? 0,
            regionName: d.regionName ?? "",
            projectId: d.projectId ?? 0,
            projectName: d.projectName ?? "",
            type: d.type ?? "",
            fromdate: d.fromdate ?? "",
            todate: d.todate ?? "",
            month: d.month ?? "",
            year: d.year ?? "",
            totalHours: d.totalHours ?? 0,
            totalCharge: d.totalCharge ?? 0,
            
            rigs:
              d.rigs?.map((rig: any) => ({
                rigName: rig.rigName ?? "",
                rows:
                  rig.rows?.map((r: any) => ({
                    type: r.type ?? "",
                    familyName: r.familyName ?? null,
                    assetName: r.assetName ?? "",
                    owner: r.owner ?? "",
                    groupCode: r.groupCode ?? "",
                    harmony: r.harmony ?? "",
                    description: r.description ?? "",
                    totalCharge: r.totalCharge ?? null,
                    timesheet: r.timesheet ?? {},
                  })) ?? [],
              })) ?? [],
          };

          setData(normalizedData);
          // setData(normalizedData);
          console.log("Fetched normalizedData:", normalizedData);

        } else {
          console.error("Failed to fetch timesheet:", response.status.message);
        }
      } catch (err) {
        console.error("Error fetching timesheet:", err);
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchTimesheet();
  }, [timeSheetId, dispatch]);


const handleDownloadPDF = (formData: any) => {
  console.log("🧾 Timesheet PDF Data:", formData); // ✅ Debugging

  const doc = new jsPDF("p", "mm", "a4");

  // === Helper functions ===
  const formatDateWithSuffix = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}${suffix} ${month} ${year}`;
  };

  const from = formatDateWithSuffix(formData.fromdate);
  const to = formatDateWithSuffix(formData.todate);

  // === TITLE ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Timesheet Details", 105, 18, { align: "center" });

  // === TIMESHEET INFO ===
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  const leftX = 20;
  let y = 32;

  const infoLines = [
    `Project: ${formData.projectName || "-"}`,
    `Region: ${formData.regionName || "-"} (${formData.countryName || "-"})`,
    `Type: ${formData.type || "-"}`,
    `Period: ${from} to ${to}`,
  ];

  infoLines.forEach((line) => {
    doc.text(line, leftX, y);
    y += 8;
  });

  y += 6; // space before rigs

  // === RIG DETAILS ===
  (formData.rigs || []).forEach((rig: any, index: number) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`Rig Name: ${rig.rigName || `#${index + 1}`}`, leftX, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [[
        "Type",
        "Family Name",
        "Asset Name",
        "Owner",
        "Group Code",
        "Harmony",
        "Description",
        "Total Charge",
      ]],
      body: (rig.rows || []).map((r: any) => [
        r.type || "-",
        r.familyName ?? "-",
        r.assetName || "-",
        r.owner || "-",
        r.groupCode || "-",
        r.harmony || "-",
        r.description || "-",
        typeof r.totalCharge === "number"
          ? r.totalCharge.toFixed(2)
          : parseFloat(r.totalCharge || "0").toFixed(2),
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 121, 107], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        2: { cellWidth: 35 },
        6: { cellWidth: 40 },
      },
      theme: "grid",
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  });
  // === TOTAL SUMMARY ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);

  doc.text(`Total Hours: ${formData.totalHours ?? 0}`, 20, y);
  y += 8;

  doc.text(
    `Total Charge: ${formData.totalCharge ?? 0}`,
    20,
    y
  );


  // === FOOTER ===
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text("Generated by MeteoriQs Timesheet System", 105, 290, { align: "center" });

  // === SAVE ===
  doc.save(`Timesheet_${formData.projectName || "Details"}.pdf`);
};

const handleDownloadExcel = (data: TimesheetData | null) => {
  if (!data) return;

  const workbook = XLSX.utils.book_new();

  const formattedFrom = new Date(data.fromdate).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTo = new Date(data.todate).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const period = `${formattedFrom} to ${formattedTo}`;

  data.rigs.forEach((rig, index) => {
    const sheetData: (string | number)[][] = [];

    // ✅ Header info
    sheetData.push(["Timesheet Details"]);
    sheetData.push([]);
    sheetData.push(["Project Name:", data.projectName || "-"]);
    sheetData.push(["Region Name:", data.regionName || "-"]);
    sheetData.push(["Country Name:", data.countryName || "-"]);
    sheetData.push(["Type:", data.type || "-"]);
    sheetData.push(["Period:", period]);
    sheetData.push(["Rig Name:", rig.rigName || "-"]);
    sheetData.push([]);

    // ✅ Table header
    sheetData.push([
      "Type",
      "Family Name",
      "Asset Name",
      "Owner",
      "Group Code",
      "Harmony",
      "Description",
      "Total Charge",
    ]);

    // ✅ Table rows
    rig.rows.forEach((r) => {
      const timesheetStr =
        r.timesheet && Object.keys(r.timesheet).length > 0
          ? Object.entries(r.timesheet)
              .map(([date, hrs]) => `${date} : ${hrs}`)
              .join(", ")
          : "-";

      sheetData.push([
        r.type || "-",
        r.familyName ?? "",
        r.assetName || "-",
        r.owner || "-",
        r.groupCode || "-",
        r.harmony || "-",
        r.description || "-",
        typeof r.totalCharge === "number"
          ? r.totalCharge.toFixed(2)
          : parseFloat(r.totalCharge || "0").toFixed(2),
      ]);
    });
    sheetData.push([]);
    sheetData.push(["TOTAL HOURS", data.totalHours ?? 0]);
    sheetData.push(["TOTAL CHARGE", data.totalCharge ?? 0]);


    // ✅ Convert to worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // ✅ Append worksheet with distinct rig name or index if duplicate
    const sheetName = rig.rigName
      ? rig.rigName.substring(0, 31) // Excel sheet name limit
      : `Rig_${index + 1}`;

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  // ✅ Save file
  const fileName = `Timesheet_${data.projectName.replace(/\s/g, "_")}_${formattedFrom}_to_${formattedTo}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

  if (!data) {
    return (
      <Typography variant="body1" sx={{ p: 4 }}>
        Loading timesheet details...
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header & Back */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Timesheet Details
        </Typography>

        <Stack direction="row" spacing={2}>
         <Button variant="contained" onClick={() => handleDownloadPDF(data)}>Download PDF</Button>           
 

            <Button
            variant="outlined"
            color="success"
            onClick={() => handleDownloadExcel(data)} // ✅ correct
            >
            Download Excel
            </Button>


          <Button variant="contained" color="secondary" onClick={() => setOpen(false)}>
            Back to List
          </Button>
        </Stack>
      </Box>

      {/* Header Info */}
      <Typography variant="body1" gutterBottom>
        Project: {data.projectName}
      </Typography>
      <Typography variant="body1">
        Region: {data.regionName} ({data.countryName})
      </Typography>
      <Typography variant="body1">Type: {data.type}</Typography>
      <Typography variant="body1">
        Period: {data.month} {data.year}
      </Typography>
      <Typography variant="body1">
      Total Hours: {data.totalHours}
      </Typography>
      <Typography variant="body1">
        Total Charge: {data.totalCharge}
      </Typography>


      <Divider sx={{ my: 3 }} />

      {/* Rigs and Assets */}
      {data.rigs.map((rig, rigIndex) => (
        <Card key={rigIndex} sx={{ mb: 4, boxShadow: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" fontWeight="bold">
              Rig Name: {rig.rigName}
            </Typography>

            <Table sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Family Asset ID</b></TableCell>
                  <TableCell><b>Asset Name</b></TableCell>
                  <TableCell><b>Owner</b></TableCell>
                  <TableCell><b>Group Code</b></TableCell>
                  <TableCell><b>Harmony</b></TableCell>
                  <TableCell><b>Description</b></TableCell>
                  <TableCell align="right"><b>Total Charge</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rig.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.familyName}</TableCell>
                    <TableCell>{row.assetName}</TableCell>
                    <TableCell>{row.owner}</TableCell>
                    <TableCell>{row.groupCode}</TableCell>
                    <TableCell>{row.harmony}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">
                      {row.totalCharge ?? "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default TimesheetView;
